import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for AI analysis
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Analyze food image
 * @param {string} accessToken - Access token
 * @param {string} imageBase64 - Base64 encoded image
 * @param {string} language - Language code ('uz' or 'en')
 * @returns {Promise<Object>} Food analysis result
 */
export const analyzeFood = async (accessToken, imageBase64, language = 'uz') => {
  const response = await apiClient.post(
    '/api/food/analyze',
    {
      image_base64: imageBase64,
      language: language,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};

/**
 * Get food analysis history
 * @param {string} accessToken - Access token
 * @param {number} page - Page number (starts from 1)
 * @param {number} limit - Number of items to return
 * @returns {Promise<Array>} List of food analyses
 */
export const getFoodHistory = async (accessToken, page = 1, limit = 20) => {
  const response = await apiClient.get('/api/food/history', {
    params: { page, limit },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  // Backend returns { total, page, limit, items }
  // We need to return items array and add nutrition object if missing
  const items = response.data.items || [];
  return items.map(item => ({
    ...item,
    // Ensure nutrition object exists for frontend compatibility
    nutrition: item.nutrition || {
      calories: item.calories || 0,
      protein: item.protein || 0,
      carbs: item.carbs || 0,
      fat: item.fat || 0
    }
  }));
};

/**
 * Get single food analysis
 * @param {string} accessToken - Access token
 * @param {number} analysisId - Analysis ID
 * @returns {Promise<Object>} Food analysis
 */
export const getFoodAnalysis = async (accessToken, analysisId) => {
  const response = await apiClient.get(`/api/food/history/${analysisId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

/**
 * Delete food analysis
 * @param {string} accessToken - Access token
 * @param {number} analysisId - Analysis ID
 * @returns {Promise<Object>} Delete response
 */
export const deleteFoodAnalysis = async (accessToken, analysisId) => {
  const response = await apiClient.delete(`/api/food/history/${analysisId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

/**
 * Toggle favorite status
 * @param {string} accessToken - Access token
 * @param {number} analysisId - Analysis ID
 * @param {boolean} isFavorite - Favorite status
 * @returns {Promise<Object>} Updated analysis
 */
export const toggleFavorite = async (accessToken, analysisId, isFavorite) => {
  const response = await apiClient.put(
    `/api/food/history/${analysisId}/favorite`,
    { is_favorite: isFavorite },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};

/**
 * Log meal to daily nutrition
 * @param {string} accessToken - Access token
 * @param {number} analysisId - Analysis ID
 * @param {string} mealType - Type of meal (breakfast, lunch, dinner, snack)
 * @returns {Promise<Object>} Logged meal response
 */
export const logMeal = async (accessToken, analysisId, mealType) => {
  const response = await apiClient.post(
    `/api/food/history/${analysisId}/log-meal`,
    { meal_type: mealType },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};
